import React from 'react';
import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { OrganizationSecretItems } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { DataSetType } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { DataSetIntegrationConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetIntegrationConfigurationComponent';
import { DataSetSupplementalConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSupplementalConfigurationComponent';
import { DataSetFileConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/DataSetFileConfigurationComponent';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { DataSetEditHTTPIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditHTTPIntegration';
import { DataSetEditMergeIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditMergeIntegration';
import { DataSetEditPostgresIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPostgresIntegration';
import { DataSetEditSqlServerIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSqlServerIntegration';
import { DataSetIntegrationFieldValidationError } from 'libs/end-to-end/src/data-sets';
import { DataSetEditCompositeHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditComposite';
import { DataSetCompositeConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/DataSetCompositeConfigurationComponent';
import { DbtAction } from 'libs/end-to-end/src/types/databases/integrations/snowflake-integration';
import { DataSetEditSupplementalHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSupplemental';

export const DataSetConfigurationComponent = ({
  dataSetType,
  fileName,
  fileNameLabel,
  setFileName,
  integrationType,
  clientIdSecretReference,
  clientSecretSecretReference,
  usernameSecretReference,
  passwordSecretReference,
  folderIdSecretReference,
  fileNameSecretReference,
  setClientIdSecretReference,
  setClientSecretSecretReference,
  setUsernameSecretReference,
  setPasswordSecretReference,
  setFolderIdSecretReference,
  setFileNameSecretReference,
  apiKeySecretReference,
  setApiKeySecretReference,
  organizationId,
  listSecretsPattern,
  setEmployeeHistoryUrlSecretReference,
  setStaffingEventsUrlSecretReference,
  setCompensationChangeHistoryUrlSecretReference,
  setCandidatesUrlSecretReference,
  compensationChangeHistoryUrlSecretReference,
  staffingEventsUrlSecretReference,
  candidatesUrlSecretReference,
  employeeHistoryUrlSecretReference,
  oauthUrlSecretReference,
  setOauthUrlSecretReference,
  setApiBaseUrlSecretReference,
  apiBaseUrlSecretReference,
  httpIntegrationHook,
  postgresIntegrationHook,
  sqlServerIntegrationHook,
  mergeIntegrationHook,
  compositeHook,
  tryCreateMergeMagicLink,
  integrationFieldValidationErrors,
  sslCertSecretReference,
  setKeyCertSecretReference,
  setSslCertSecretReference,
  keyCertSecretReference,
  dbtAction,
  setDbtAction,
  supplementalHook,
  agentMode,
  setAgentMode,
  agentDateCap,
  setAgentDateCap,
}: {
  dataSetType: DataSetType;
  fileName: string;
  fileNameLabel: string;
  setFileName: Setter<string>;
  organizationId: string;
  integrationType: IntegrationType;
  clientIdSecretReference: SecretReference | null;
  clientSecretSecretReference: SecretReference | null;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  folderIdSecretReference: SecretReference | null;
  fileNameSecretReference: SecretReference | null;
  setClientIdSecretReference: Setter<SecretReference | null>;
  setClientSecretSecretReference: Setter<SecretReference | null>;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setPasswordSecretReference: Setter<SecretReference | null>;
  setFolderIdSecretReference: Setter<SecretReference | null>;
  setFileNameSecretReference: Setter<SecretReference | null>;
  apiKeySecretReference: SecretReference | null;
  setApiKeySecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
  staffingEventsUrlSecretReference: SecretReference | null;
  setStaffingEventsUrlSecretReference: Setter<SecretReference | null>;
  setEmployeeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setCompensationChangeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setCandidatesUrlSecretReference: Setter<SecretReference | null>;
  employeeHistoryUrlSecretReference: SecretReference | null;
  compensationChangeHistoryUrlSecretReference: SecretReference | null;
  candidatesUrlSecretReference: SecretReference | null;
  oauthUrlSecretReference: SecretReference | null;
  setOauthUrlSecretReference: Setter<SecretReference | null>;
  apiBaseUrlSecretReference: SecretReference | null;
  setApiBaseUrlSecretReference: Setter<SecretReference | null>;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  compositeHook: DataSetEditCompositeHook;
  tryCreateMergeMagicLink: () => void;
  integrationFieldValidationErrors: DataSetIntegrationFieldValidationError[];
  sslCertSecretReference: SecretReference | null;
  keyCertSecretReference: SecretReference | null;
  setSslCertSecretReference: Setter<SecretReference | null>;
  setKeyCertSecretReference: Setter<SecretReference | null>;
  dbtAction: DbtAction;
  setDbtAction: Setter<DbtAction>;
  supplementalHook: DataSetEditSupplementalHook;
  agentMode: boolean | null;
  setAgentMode: Setter<boolean>;
  agentDateCap: string;
  setAgentDateCap: Setter<string>;
}) => {
  if (dataSetType === 'Integration') {
    if (integrationType === null) {
      return <></>;
    }

    return (
      <DataSetIntegrationConfigurationComponent
        fileNameSecretReference={fileNameSecretReference}
        setFileNameSecretReference={setFileNameSecretReference}
        clientIdSecretReference={clientIdSecretReference}
        setClientIdSecretReference={setClientIdSecretReference}
        clientSecretSecretReference={clientSecretSecretReference}
        setClientSecretSecretReference={setClientSecretSecretReference}
        usernameSecretReference={usernameSecretReference}
        setUsernameSecretReference={setUsernameSecretReference}
        passwordSecretReference={passwordSecretReference}
        setPasswordSecretReference={setPasswordSecretReference}
        folderIdSecretReference={folderIdSecretReference}
        setFolderIdSecretReference={setFolderIdSecretReference}
        apiKeySecretReference={apiKeySecretReference}
        setApiKeySecretReference={setApiKeySecretReference}
        staffingEventsUrlSecretReference={staffingEventsUrlSecretReference}
        setStaffingEventsUrlSecretReference={
          setStaffingEventsUrlSecretReference
        }
        setEmployeeHistoryUrlSecretReference={
          setEmployeeHistoryUrlSecretReference
        }
        setCompensationChangeHistoryUrlSecretReference={
          setCompensationChangeHistoryUrlSecretReference
        }
        setCandidatesUrlSecretReference={setCandidatesUrlSecretReference}
        employeeHistoryUrlSecretReference={employeeHistoryUrlSecretReference}
        compensationChangeHistoryUrlSecretReference={
          compensationChangeHistoryUrlSecretReference
        }
        candidatesUrlSecretReference={candidatesUrlSecretReference}
        oauthUrlSecretReference={oauthUrlSecretReference}
        setOauthUrlSecretReference={setOauthUrlSecretReference}
        apiBaseUrlSecretReference={apiBaseUrlSecretReference}
        setApiBaseUrlSecretReference={setApiBaseUrlSecretReference}
        httpIntegrationHook={httpIntegrationHook}
        postgresIntegrationHook={postgresIntegrationHook}
        sqlServerIntegrationHook={sqlServerIntegrationHook}
        listSecretsPattern={listSecretsPattern}
        integrationType={integrationType}
        organizationId={organizationId}
        mergeIntegrationHook={mergeIntegrationHook}
        tryCreateMergeMagicLink={tryCreateMergeMagicLink}
        integrationFieldValidationErrors={integrationFieldValidationErrors}
        sslCertSecretReference={sslCertSecretReference}
        setKeyCertSecretReference={setKeyCertSecretReference}
        setSslCertSecretReference={setSslCertSecretReference}
        keyCertSecretReference={keyCertSecretReference}
        dbtAction={dbtAction}
        setDbtAction={setDbtAction}
        agentMode={agentMode}
        setAgentMode={setAgentMode}
        agentDateCap={agentDateCap}
        setAgentDateCap={setAgentDateCap}
      />
    );
  }

  if (dataSetType === 'Supplemental') {
    return (
      <DataSetSupplementalConfigurationComponent
        supplementalHook={supplementalHook}
      />
    );
  }

  if (dataSetType === 'File') {
    return (
      <DataSetFileConfigurationComponent
        fileName={fileName}
        fileNameLabel={fileNameLabel}
        setFileName={setFileName}
      />
    );
  }

  if (dataSetType === 'Composite') {
    return (
      <DataSetCompositeConfigurationComponent
        compositeHook={compositeHook}
        integrationFieldValidationErrors={integrationFieldValidationErrors}
      />
    );
  }

  throw guardExhaustive(dataSetType);
};
